import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scale, ChevronLeft } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

/**
 * Public-facing Terms of Service. Accessible at /terms without auth so
 * it can be linked from the privacy policy, the in-app menu, and the
 * App Store / Play Console submission forms.
 *
 * NOT LEGAL ADVICE. This file ships a reasonable contractor-SaaS baseline
 * — disclaimers for AI estimate inaccuracy, no-warranty for third-party
 * AI providers, limitation of liability, indemnity, governing law placeholder,
 * subscription / refund terms, acceptable-use, account termination. Get a
 * licensed lawyer to review before launch if you take on enterprise users.
 *
 * When you change anything material, bump LAST_UPDATED and add a one-line
 * changelog entry inside §17 below.
 */
const LAST_UPDATED = "February 8, 2026";
const GOVERNING_JURISDICTION = "the Province of Ontario, Canada";
const COMPANY_NAME = "Steeltooth";
const CONTACT_EMAIL = "support@planflow.pro";
const LEGAL_EMAIL = "legal@planflow.pro";

const SECTIONS = [
    { id: "acceptance",   label: "1. Acceptance of these terms" },
    { id: "eligibility",  label: "2. Eligibility & account" },
    { id: "service",      label: "3. What Steeltooth is (and isn't)" },
    { id: "ai-disclaimer",label: "4. AI estimates — the big disclaimer" },
    { id: "subscriptions",label: "5. Subscriptions, billing & refunds" },
    { id: "credits",      label: "6. AI credits & fair use" },
    { id: "content",      label: "7. Your content & licence to us" },
    { id: "acceptable",   label: "8. Acceptable use" },
    { id: "third-party",  label: "9. Third-party services" },
    { id: "ip",           label: "10. Intellectual property" },
    { id: "warranty",     label: "11. Warranty disclaimer" },
    { id: "liability",    label: "12. Limitation of liability" },
    { id: "indemnity",    label: "13. Indemnification" },
    { id: "termination",  label: "14. Suspension & termination" },
    { id: "law",          label: "15. Governing law & disputes" },
    { id: "changes",      label: "16. Changes to these terms" },
    { id: "contact",      label: "17. How to reach us" },
];

export default function TermsOfServicePage() {
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Terms of Service — Steeltooth";
    }, []);

    return (
        <div
            className="h-[100dvh] w-full overflow-y-auto bg-[#0A1128] text-slate-200 flex flex-col"
            data-testid="terms-page"
        >
            <header className="sticky top-0 z-30 border-b border-[#2A3655] bg-[#0A1128]/95 backdrop-blur-xl">
                <div className="max-w-4xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
                        data-testid="terms-back-button"
                        className="flex items-center gap-1.5 text-[12px] uppercase tracking-wider font-bold text-slate-400 hover:text-white transition-colors"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back
                    </button>
                    <Link to="/" className="flex items-center" data-testid="terms-brand">
                        <BrandLogo />
                    </Link>
                </div>
            </header>

            <main className="flex-1 px-4 sm:px-8 py-8 sm:py-12 pb-44 max-w-4xl mx-auto w-full">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#FFB020] font-bold">
                    <Scale className="w-3.5 h-3.5" />
                    Terms of Service
                </div>
                <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-white">
                    The rules of the road
                </h1>
                <p className="mt-3 text-sm text-slate-400">
                    Last updated: <span className="text-slate-200 font-semibold">{LAST_UPDATED}</span>
                </p>

                <div className="mt-6 rounded-md border border-amber-500/40 bg-amber-500/10 p-4 text-[13px] text-amber-100 leading-relaxed" data-testid="terms-tldr">
                    <strong className="text-amber-200">Read this first.</strong> Steeltooth produces
                    AI-assisted construction estimates as a <em>starting point</em> for your work.
                    Plans, materials, codes and pricing vary by jurisdiction and site conditions, and
                    AI models make mistakes. <strong>You are solely responsible for reviewing every
                    estimate, takeoff and quote before sending it to a customer or relying on it to
                    bid a job.</strong> By using the service you accept these terms, including the
                    limitation of liability in §12 and the AI disclaimer in §4.
                </div>

                <nav
                    className="mt-8 rounded-md border border-[#2A3655] bg-[#0F1B3A] p-4"
                    aria-label="Table of contents"
                    data-testid="terms-toc"
                >
                    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                        Contents
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                        {SECTIONS.map((s) => (
                            <li key={s.id}>
                                <a href={`#${s.id}`} className="text-[13px] text-slate-300 hover:text-[#00A3FF]">
                                    {s.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <article className="mt-10 space-y-10 text-[15px] leading-relaxed">

                    <Section id="acceptance" title="1. Acceptance of these terms">
                        <p>
                            These Terms of Service ("<strong>Terms</strong>") form a binding
                            agreement between you and {COMPANY_NAME} ("we", "us", "our"). By
                            creating an account, signing in, or using any part of Steeltooth
                            (the "<strong>Service</strong>"), you confirm that:
                        </p>
                        <ul>
                            <li>you are at least 18 years old (or the age of majority where you live);</li>
                            <li>you have read, understood and agree to be bound by these Terms and our <Link to="/privacy" className="text-[#00A3FF] hover:underline">Privacy Policy</Link>; and</li>
                            <li>if you're signing up on behalf of a company, you have authority to bind that company.</li>
                        </ul>
                        <p>If you don't agree, don't use the Service.</p>
                    </Section>

                    <Section id="eligibility" title="2. Eligibility & account">
                        <ul>
                            <li>You're responsible for keeping your password and 2FA factors secret. Anything done through your account is treated as done by you.</li>
                            <li>Tell us immediately at <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#00A3FF] hover:underline">{CONTACT_EMAIL}</a> if you suspect unauthorised access.</li>
                            <li>Don't share a single account between multiple people — each user needs their own login (workgroups exist precisely for this).</li>
                            <li>We can refuse, suspend or terminate any account at our discretion if these Terms are broken (see §14).</li>
                        </ul>
                    </Section>

                    <Section id="service" title="3. What Steeltooth is (and isn't)">
                        <p>
                            Steeltooth is a software-as-a-service tool for contractors. It lets
                            you upload construction plans, run AI-assisted takeoffs and estimates,
                            store pricing templates, generate customer-facing quotes, manage
                            leads and contacts, and share work with teammates via workgroups.
                        </p>
                        <p>It is <strong>not</strong>:</p>
                        <ul>
                            <li>a substitute for a licensed estimator, architect, engineer, surveyor, building official or attorney;</li>
                            <li>a guarantee that any estimate, quantity, code citation or price is accurate; or</li>
                            <li>certified for use in regulated submissions (permits, sealed drawings, court filings).</li>
                        </ul>
                    </Section>

                    <Section id="ai-disclaimer" title="4. AI estimates — the big disclaimer">
                        <p>
                            The Service relies on third-party large-language-model providers
                            (Anthropic Claude, OpenAI, Google Gemini, and xAI Grok) to interpret
                            plans and produce estimates, takeoffs and quotes. AI is probabilistic.
                            It will sometimes:
                        </p>
                        <ul>
                            <li>misread dimensions, scales or notes on a plan,</li>
                            <li>miss items entirely,</li>
                            <li>price items based on outdated or wrong market rates,</li>
                            <li>hallucinate codes, brand names or product specs that don't exist,</li>
                            <li>get math wrong (especially totals, taxes and tiered pricing).</li>
                        </ul>
                        <p>
                            <strong>You are responsible for reviewing every AI-generated output
                            line-by-line before sending it to a customer, ordering materials,
                            scheduling labour or relying on it for any business decision.</strong>
                            Treat the AI output as a fast first draft, not a finished deliverable.
                        </p>
                        <p>
                            We make <strong>no representation or warranty</strong> that the AI's
                            output is accurate, complete, fit for any particular jurisdiction's
                            building code, suitable for any specific project, or current with
                            today's material prices. To the maximum extent allowed by law, we
                            disclaim all liability for losses, damages, missed bids, code
                            violations, profit losses, change orders, schedule overruns or
                            customer disputes arising from your reliance on AI-generated content
                            in the Service. See §12.
                        </p>
                    </Section>

                    <Section id="subscriptions" title="5. Subscriptions, billing & refunds">
                        <ul>
                            <li><strong>Plans &amp; pricing.</strong> Subscription tiers, prices and included AI credits are listed in the in-app Subscriptions page and may change with 30 days' notice. Your current tier is locked in for the duration of your current billing period.</li>
                            <li><strong>Payment processor.</strong> Billing is handled by RevenueCat → Stripe for web purchases, and by Apple App Store / Google Play for in-app purchases on iOS / Android. We never see or store your full card number.</li>
                            <li><strong>Auto-renewal.</strong> Subscriptions renew automatically at the start of each billing period unless cancelled at least 24 hours before the renewal date through the store you bought them from.</li>
                            <li><strong>Refunds.</strong> Because the Service consumes third-party AI compute the moment you run an estimate, <strong>all sales are final once any AI feature is used in a billing period</strong>. If you have not used any AI feature and contact us within 14 days of charge, we'll refund that period at our discretion. App Store / Play Store refunds are governed by Apple's / Google's policies — request those through the store directly.</li>
                            <li><strong>Taxes.</strong> Prices exclude applicable taxes. You're responsible for any tax, GST/HST/VAT or duty payable in your jurisdiction.</li>
                            <li><strong>Failed payment.</strong> If a charge fails we may suspend AI features after 7 days and read-only your account after 30 days of non-payment.</li>
                        </ul>
                    </Section>

                    <Section id="credits" title="6. AI credits & fair use">
                        <ul>
                            <li>Each tier includes a monthly bucket of AI credits. Credits are consumed per-call at rates published in the Subscriptions page.</li>
                            <li>Credits do <strong>not</strong> roll over to the next billing period and have no cash value.</li>
                            <li>We may rate-limit, throttle or temporarily disable AI features to protect platform stability or to prevent abuse — for example, scripted requests, scraping, or attempts to extract our prompt templates.</li>
                            <li>Don't try to bypass credit metering by spinning up multiple accounts, exchanging credits between users, or sharing one login across a crew. We track usage and reserve the right to claw back credits obtained that way and to suspend involved accounts.</li>
                        </ul>
                    </Section>

                    <Section id="content" title="7. Your content & the licence you grant us">
                        <p>
                            "Your Content" means everything you upload or create inside the
                            Service: plan PDFs, takeoffs, estimates, quotes, templates, leads,
                            contact lists, chats, logos and so on.
                        </p>
                        <ul>
                            <li><strong>You own it.</strong> We don't claim ownership over Your Content.</li>
                            <li><strong>You grant us a licence</strong> to host, store, transmit, transform, render and back up Your Content for the sole purpose of running the Service for you — including sending the parts you submit to AI providers when you trigger an AI feature.</li>
                            <li>The licence ends within 30 days of you deleting the content or your account, except for back-up copies and legally-required records.</li>
                            <li><strong>You promise</strong> Your Content is yours to upload — no copyrighted plans you don't have rights to, no personal data of others you can't legally process, no malware in attachments.</li>
                        </ul>
                    </Section>

                    <Section id="acceptable" title="8. Acceptable use">
                        <p>Don't use the Service to:</p>
                        <ul>
                            <li>violate any law, code, regulation or court order;</li>
                            <li>infringe anyone's intellectual property, privacy or publicity rights;</li>
                            <li>build a competing service by reverse-engineering, scraping our prompts, or training an AI model on our outputs;</li>
                            <li>upload malware, exploit a vulnerability, or run automated load against the API beyond rate limits;</li>
                            <li>impersonate another person or company;</li>
                            <li>publish customer-facing quotes you know to be deceptive or illegal in the destination jurisdiction.</li>
                        </ul>
                        <p>We may remove offending content and suspend involved accounts without warning if continued use risks harming us, other customers or third parties.</p>
                    </Section>

                    <Section id="third-party" title="9. Third-party services">
                        <p>
                            The Service relies on third parties (listed in our <Link to="/privacy#subprocessors" className="text-[#00A3FF] hover:underline">Privacy Policy §6</Link>) to function:
                            AI providers, payments, email, hosting, identity. We use commercially
                            reasonable efforts to choose reputable providers, but we don't control
                            them and aren't responsible for their outages, errors or pricing
                            changes. If a third party stops offering its service, we may need to
                            substitute it or remove the dependent feature.
                        </p>
                    </Section>

                    <Section id="ip" title="10. Intellectual property">
                        <p>
                            All Service software, branding, UI design, copy, prompt templates,
                            documentation and code are owned by {COMPANY_NAME} or its licensors
                            and protected by copyright and trademark law. The licence we grant
                            you is limited: you can use the Service through the normal in-app
                            interface for your own contracting business. That's it — no
                            reselling, sublicensing, scraping, reverse-engineering or training
                            other AIs on our outputs.
                        </p>
                        <p>
                            <strong>Feedback.</strong> If you send us a suggestion or feature
                            request, you grant us a perpetual, royalty-free, irrevocable licence
                            to use it without attribution or compensation.
                        </p>
                    </Section>

                    <Section id="warranty" title="11. Warranty disclaimer">
                        <p>
                            THE SERVICE IS PROVIDED <strong>"AS IS"</strong> AND <strong>"AS AVAILABLE"</strong>, WITH ALL FAULTS,
                            AND WITHOUT ANY WARRANTY OF ANY KIND. TO THE MAXIMUM EXTENT
                            PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED,
                            INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                            PARTICULAR PURPOSE, ACCURACY, NON-INFRINGEMENT AND ANY WARRANTIES
                            ARISING OUT OF COURSE OF DEALING OR USAGE OF TRADE. WE DO NOT
                            WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE,
                            OR FREE OF VIRUSES, OR THAT AI-GENERATED OUTPUTS WILL BE ACCURATE
                            OR FIT FOR ANY USE.
                        </p>
                    </Section>

                    <Section id="liability" title="12. Limitation of liability">
                        <p>
                            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL {COMPANY_NAME.toUpperCase()},
                            ITS OFFICERS, EMPLOYEES, AGENTS OR LICENSORS BE LIABLE FOR ANY
                            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY OR PUNITIVE
                            DAMAGES — INCLUDING LOST PROFITS, LOST CONTRACTS, LOST DATA, BUSINESS
                            INTERRUPTION, COST OF SUBSTITUTE GOODS OR SERVICES, OR LOSSES
                            ARISING FROM RELIANCE ON AI-GENERATED ESTIMATES, TAKEOFFS OR QUOTES
                            — EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                        </p>
                        <p>
                            OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF
                            OR RELATING TO THE SERVICE OR THESE TERMS IS LIMITED TO THE GREATER
                            OF (a) THE FEES YOU PAID US IN THE 12 MONTHS BEFORE THE EVENT
                            GIVING RISE TO THE CLAIM, OR (b) ONE HUNDRED CANADIAN DOLLARS (CA$100).
                        </p>
                        <p>
                            Some jurisdictions don't allow the exclusion or limitation of certain
                            damages, so parts of this section may not apply to you. In that
                            case our liability is limited to the smallest amount allowed by law.
                        </p>
                    </Section>

                    <Section id="indemnity" title="13. Indemnification">
                        <p>
                            You agree to defend, indemnify and hold harmless {COMPANY_NAME} and
                            its officers, employees, agents and licensors from and against any
                            and all claims, damages, obligations, losses, liabilities, costs and
                            expenses (including reasonable legal fees) arising out of:
                        </p>
                        <ul>
                            <li>your use of the Service, including any estimate, takeoff or quote you produced with the Service and delivered to a customer;</li>
                            <li>your breach of these Terms;</li>
                            <li>your violation of any third-party right, including intellectual property, privacy or contractual rights;</li>
                            <li>any content you upload, including claims that it infringes the rights of a third party.</li>
                        </ul>
                    </Section>

                    <Section id="termination" title="14. Suspension & termination">
                        <ul>
                            <li><strong>By you.</strong> Cancel any time from <strong>Settings → Subscriptions</strong>; you keep access through the end of the billing period you've paid for. Delete your account from <strong>Settings → Account</strong>.</li>
                            <li><strong>By us.</strong> We can suspend or terminate your account for material breach of these Terms (including non-payment, fraud, abuse, illegal use). We'll give you reasonable notice and a chance to cure when practical.</li>
                            <li><strong>Effect of termination.</strong> Your right to use the Service ends. Sections that by their nature should survive (warranty disclaimer, liability cap, indemnity, governing law, IP, surviving payment obligations) survive termination.</li>
                            <li><strong>Data export.</strong> You have 30 days after termination to export Your Content through the in-app export tool before it's permanently deleted.</li>
                        </ul>
                    </Section>

                    <Section id="law" title="15. Governing law & dispute resolution">
                        <p>
                            These Terms are governed by the laws of {GOVERNING_JURISDICTION},
                            without regard to conflict-of-law rules. The UN Convention on
                            Contracts for the International Sale of Goods doesn't apply.
                        </p>
                        <p>
                            <strong>Informal resolution first.</strong> Before filing anything,
                            email <a href={`mailto:${LEGAL_EMAIL}`} className="text-[#00A3FF] hover:underline">{LEGAL_EMAIL}</a> describing
                            the dispute. We'll try to work it out in good faith for 30 days.
                        </p>
                        <p>
                            <strong>Court jurisdiction.</strong> If informal resolution fails,
                            you and we agree to the exclusive jurisdiction of the courts located
                            in {GOVERNING_JURISDICTION} for any dispute arising out of or relating
                            to these Terms or the Service, except that either party may seek
                            injunctive relief in any court of competent jurisdiction to protect
                            its IP or confidential information.
                        </p>
                        <p>
                            <strong>No class actions.</strong> To the extent permitted by law,
                            you agree to bring any claim only individually, not as part of a
                            class, collective or representative action.
                        </p>
                    </Section>

                    <Section id="changes" title="16. Changes to these terms">
                        <p>
                            We may update these Terms from time to time. If a change is material,
                            we'll notify you by email and via an in-app banner at least 14 days
                            before it takes effect. Your continued use of the Service after the
                            effective date counts as acceptance. If you don't agree, cancel
                            your subscription before the effective date.
                        </p>
                    </Section>

                    <Section id="contact" title="17. How to reach us">
                        <ul>
                            <li>General support: <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#00A3FF] hover:underline">{CONTACT_EMAIL}</a></li>
                            <li>Legal notices: <a href={`mailto:${LEGAL_EMAIL}`} className="text-[#00A3FF] hover:underline">{LEGAL_EMAIL}</a></li>
                            <li>Mail: {COMPANY_NAME}, c/o the account owner listed on your invoice.</li>
                        </ul>
                        <p className="text-[12px] text-slate-500 mt-6">
                            These Terms reference and incorporate our <Link to="/privacy" className="text-[#00A3FF] hover:underline">Privacy Policy</Link>.
                            If anything in this document conflicts with the Privacy Policy on a
                            privacy-specific question, the Privacy Policy governs.
                        </p>
                    </Section>

                </article>
            </main>
        </div>
    );
}

function Section({ id, title, children }) {
    return (
        <section
            id={id}
            data-testid={`terms-section-${id}`}
            className="scroll-mt-20"
        >
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-3">
                {title}
            </h2>
            <div className="text-slate-300 space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul>li]:my-1 [&_p]:my-2">
                {children}
            </div>
        </section>
    );
}
